import { useState, useCallback } from "react";
import type { LoginFormValues, LoginFormErrors } from "../auth.types";

const INITIAL_VALUES: LoginFormValues = {
  email: "",
  password: "",
  rememberMe: false,
  acceptTerms: false,
};

interface UseLoginFormOptions {
  onSubmit: (values: LoginFormValues) => Promise<void>;
}

interface UseLoginFormReturn {
  values: LoginFormValues;
  errors: LoginFormErrors;
  isSubmitting: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCheckboxChange: ({
    name,
    checked,
  }: {
    name: string;
    checked: boolean;
  }) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setFieldValue: <K extends keyof LoginFormValues>(
    field: K,
    value: LoginFormValues[K],
  ) => void;
  clearError: (field: keyof LoginFormErrors) => void;
}

export const useLoginForm = ({
  onSubmit,
}: UseLoginFormOptions): UseLoginFormReturn => {
  const [values, setValues] = useState<LoginFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useCallback(
    (formValues: LoginFormValues): LoginFormErrors => {
      const newErrors: LoginFormErrors = {};

      if (!formValues.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
        newErrors.email = "Invalid email address";
      }

      if (!formValues.password.trim()) {
        newErrors.password = "Password is required";
      }

      if (!formValues.acceptTerms) {
        newErrors.acceptTerms = "You must accept the terms and conditions";
      }

      return newErrors;
    },
    [],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const handleCheckboxChange = useCallback(
    ({ checked, name }: { checked: boolean; name: string }) => {
      setValues((prev) => ({ ...prev, [name]: checked }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    },
    [],
  );

  const setFieldValue = useCallback(
    <K extends keyof LoginFormValues>(field: K, value: LoginFormValues[K]) => {
      setValues((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const clearError = useCallback((field: keyof LoginFormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const validationErrors = validate(values);

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        // implement your logic here
        await onSubmit(values);
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, validate, onSubmit],
  );

  return {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleCheckboxChange,
    handleSubmit,
    setFieldValue,
    clearError,
  };
};
